import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, TextArea, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { documentTemplateReducer } from "../../../state/reducers";
import { IDocumentTemplate } from "../../../utils/types/models";

const DocumentTemplateEditor: React.FC<IAddEntityScreenProps<IDocumentTemplate> | IEditEntityScreenProps<IDocumentTemplate>> = (
	props: IAddEntityScreenProps<IDocumentTemplate> | IEditEntityScreenProps<IDocumentTemplate>
) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(documentTemplateReducer, getInitialState<IDocumentTemplate>());

	const intialDocumentTemplate: IDocumentTemplate = {
		id: (props as IEditEntityScreenProps<IDocumentTemplate>).recordId || 0,
		name: "",
		description: "",
		documentNameNl: "",
		documentNameFr: "",
		documentNameDe: "",
		documentNameEn: "",
		active: true
	};

	const [documentTemplate, setDocumentTemplate] = useState<IDocumentTemplate>(intialDocumentTemplate);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setDocumentTemplate(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent | React.ChangeEvent<HTMLTextAreaElement>): void {
		setDocumentTemplate(handleChange(documentTemplate, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(documentTemplate.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		if (isNullOrEmpty(documentTemplate.documentNameNl)) {
			messages.push(t("fill_in_required_field", { field: t("documentNameNl").toLowerCase() }));
		}
		if (isNullOrEmpty(documentTemplate.documentNameFr)) {
			messages.push(t("fill_in_required_field", { field: t("documentNameFr").toLowerCase() }));
		}
		if (isNullOrEmpty(documentTemplate.documentNameDe)) {
			messages.push(t("fill_in_required_field", { field: t("documentNameDe").toLowerCase() }));
		}
		if (isNullOrEmpty(documentTemplate.documentNameEn)) {
			messages.push(t("fill_in_required_field", { field: t("documentNameEn").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IDocumentTemplate): void {
		if (record && !close) {
			setDocumentTemplate(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IDocumentTemplate>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={documentTemplate}
			endpoint={Endpoint.DocumentTemplates}
			entityState={state}
			entityType={t("documentTemplate")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={documentTemplate.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={documentTemplate.name} onChange={onChange} required disabled={readonly} />
				</label>
				<label className="k-form-field">
					<span>{t("description")}</span>
					<TextArea name="description" value={documentTemplate.description} onChange={onChange} disabled={readonly} />
				</label>
				<label className="k-form-field">
					<span>{t("documentNameNl")} *</span>
					<Input name="documentNameNl" value={documentTemplate.documentNameNl} required onChange={onChange} disabled={readonly} />
					<label className="k-form-field">
						<span>{t("documentNameDe")} *</span>
						<Input name="documentNameDe" value={documentTemplate.documentNameDe} required onChange={onChange} disabled={readonly} />
					</label>
					<label className="k-form-field">
						<span>{t("documentNameFr")} *</span>
						<Input name="documentNameFr" value={documentTemplate.documentNameFr} required onChange={onChange} disabled={readonly} />
					</label>
					<label className="k-form-field">
						<span>{t("documentNameEn")} *</span>
						<Input name="documentNameEn" value={documentTemplate.documentNameEn} required onChange={onChange} disabled={readonly} />
					</label>
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={documentTemplate.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default DocumentTemplateEditor;
